<template>
  <div class="d-flex flex-column ga-3 px-6 py-4" data-cc="bekki-composer">
    <v-textarea
      ref="input"
      v-model="bekkiChatStore.draft"
      :placeholder="t('widgets.bekki.inputPlaceholder')"
      :aria-label="t('widgets.bekki.inputPlaceholder')"
      :maxlength="BEKKI_MAX_MESSAGE_LENGTH"
      rows="1"
      max-rows="4"
      rounded="xl"
      auto-grow
      hide-details
      class="bekki-composer__input"
      data-cc="bekki-input"
      @keydown.enter.exact="onEnter"
    >
      <template v-if="hasDraft" #append-inner>
        <v-icon-btn
          icon="huge:arrow-right-02"
          color="primary"
          variant="flat"
          size="small"
          :disabled="bekkiChatStore.isStreaming"
          :aria-label="t('widgets.bekki.send')"
          data-cc="bekki-send"
          @click="submit"
        />
      </template>
    </v-textarea>

    <p class="text-caption-sm text-disabled pb-1" data-cc="bekki-ai-disclaimer">
      {{ t('widgets.bekki.aiDisclaimer') }}
    </p>
  </div>
</template>

<script setup lang="ts">
import { computed, useTemplateRef } from 'vue';

import { useI18n } from 'vue-i18n';

import { BEKKI_MAX_MESSAGE_LENGTH } from '@/composables/api/bekki/constants';
import useBekkiChatStore from '@/stores/bekkiChat';

const { t } = useI18n();

const bekkiChatStore = useBekkiChatStore();

const input = useTemplateRef('input');

const hasDraft = computed(
  (): boolean => bekkiChatStore.draft.trim().length > 0
);

const submit = (): void => {
  if (!hasDraft.value || bekkiChatStore.isStreaming) return;
  void bekkiChatStore.send(bekkiChatStore.draft);
  input.value?.focus();
};

const onEnter = (event: KeyboardEvent): void => {
  if (event.isComposing) return;
  event.preventDefault();
  submit();
};
</script>

<style scoped lang="scss">
@use 'sass:map';
@use '@/styles/variables/typography' as type;

$input-spacing: 10px;

.bekki-composer__input {
  --v-input-padding-top: #{$input-spacing};

  :deep(.v-field) {
    --v-field-padding-start: var(--bm-spacing-3);
    --v-field-padding-end: var(--bm-spacing-3);
    --v-field-padding-bottom: #{$input-spacing};
  }

  :deep(.v-field--appended) {
    --v-field-padding-end: #{$input-spacing};
  }

  :deep(.v-field__append-inner) {
    align-items: center;
    padding-top: 0;
  }

  &.v-input :deep(.v-field .v-field__input) {
    font-size: map.get(type.$typography, 'input-text', 'size');
    line-height: map.get(type.$typography, 'input-text', 'line-height');
  }

  &.v-input :deep(.v-field .v-field__input:not(.v-textarea__sizer)) {
    min-height: var(--v-input-control-height) !important;
  }
}
</style>
